<template>
    <Spinner v-if="displaySpinner" />
    <InfoMessage
        title="Welcome to your process"
        storageKey="welcome-message-process"
        :forceShow="!hasAnyContent"
        showToggle
    >
        <p>
            Processes are executed within your domain to deliver a product or
            service. They can be described and modeled in
            <a
                href="https://stekz.com/ai-at-work-visualizing-and-streamlining-your-business-processes/"
                target="_blank"
                >BPMN</a
            >
            on this page.
        </p>
        <p v-if="!process?.process_definition">
            Start by modeling your process to reflect your activities.
        </p>
    </InfoMessage>
    <Page>
        <template #title>
            <EditableTitle v-model="process" icon="pi pi-share-alt" />
        </template>
        <template #controls>
            <LastChangedInfo
                v-if="process?.identity"
                :entityId="process.identity"
                :entityType="EntityType.PROCESS"
            />
            <Button
                v-if="description && !description.content && isEditing"
                v-tooltip.bottom="{
                    value: 'Will generate a first draft of the description based on the context.',
                    showDelay: 100,
                }"
                class="p-button-secondary"
                icon="pi pi-sparkles"
                label="Generate first draft"
                @click="generateProcessDescription"
            />
        </template>
        <Section class="description" editable>
            <TextAppArtefact
                v-if="process?.identity && description"
                v-model="description"
                :isEditing="isEditing"
            />
        </Section>
        <Section class="process-definition" editable>
            <div
                v-if="isEditing && definition?.content"
                class="process-definition-overlay"
            >
                To edit the process definition,
                <Button
                    v-if="organization && stek && process"
                    severity="secondary"
                    class="maximize-button"
                    icon="pi pi-arrow-up-right-and-arrow-down-left-from-center"
                    label="Maximize"
                    size="small"
                    as="RouterLink"
                    :to="{
                        name: 'processDefinitionPage',
                        params: {
                            orgName: organization.name,
                            stekName: stek.name,
                            processId: process.identity,
                        },
                    }"
                />
                it first.
            </div>
            <template v-if="process">
                <ProcessDefinition
                    v-if="definition?.content"
                    v-model:bpmnArtefact="definition"
                    v-model:highlightElementId="highlightElementId"
                    :isNavigationEnabled="true"
                    :isScrollingEnabled="false"
                    :isMaximizingEnabled="!isEditing"
                    :isDisabled="isEditing"
                    class="main-process-definition"
                />
                <ProcessDefinitionCreator
                    v-else-if="definition"
                    v-model="definition"
                    class="main-process-definition"
                />
            </template>
        </Section>
        <Section
            v-if="
                process &&
                (processElements?.length || archivedProcessElements?.length)
            "
            editable
        >
            <Panel class="process-elements-panel" toggleable collapsed>
                <template #header>
                    <Title>Process steps</Title>
                </template>
                <ProcessElementTable
                    v-if="process"
                    v-model="highlightElementId"
                    :processElements="processElements"
                />
                <ProcessElementTable
                    class="archived-data-table"
                    v-if="
                        process &&
                        archivedProcessElements &&
                        archivedProcessElements.length > 0
                    "
                    v-model="highlightArchivedElementId"
                    :processElements="archivedProcessElements"
                    header="Archived"
                />
            </Panel>
        </Section>
        <Section editable>
            <Documents
                v-if="process"
                :isEditing="isEditing"
                entityType="process"
                :entity="process"
                :artefacts="artefacts"
            />
        </Section>
    </Page>
</template>

<script setup lang="ts">
import Documents from "@/components/Documents.vue";
import EditableTitle from "@/components/EditableTitle.vue";
import InfoMessage from "@/components/InfoMessage.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import ProcessDefinition from "@/components/Process/Definition.vue";
import ProcessDefinitionCreator from "@/components/Process/DefinitionCreator.vue";
import ProcessElementTable from "@/components/ProcessElementTable.vue";
import Section from "@/components/Section.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import Title from "@/components/Title.vue";
import { useEditManager } from "@/composables/edit-manager";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useProcessManager } from "@/composables/process-manager";
import { useStekManager } from "@/composables/stek-manager";
import { useArtefactsManager } from "@/lib/artefacts/user";
import Spinner from "@/shared/Spinner.vue";
import { EntityType } from "@/stores/entity-store";
import { useProcessElementsManager } from "@/stores/process-elements-store";
import { useTaskManager } from "@/stores/task-store";
import { computed, ref } from "vue";

useTaskManager();
const { organization } = useOrganizationManager();
const { stek } = useStekManager();
const {
    process,
    isFetching: isFetchingProcess,
    definition,
    description,
} = useProcessManager();
const { isEditing } = useEditManager();
const { artefacts } = useArtefactsManager();
const {
    processElements,
    archivedProcessElements,
    isFetching: isFetchingProcessElements,
} = useProcessElementsManager();
const { generateContent } = useGenerateDescriptionDialog();
const isLoading = ref(false);

const displaySpinner = computed(() => {
    return Boolean(
        isFetchingProcessElements.value ||
            isFetchingProcess.value ||
            isLoading.value
    );
});

const highlightElementId = ref<string | null>();
const highlightArchivedElementId = ref<string | null>();

const generateProcessDescription = async () => {
    if (!process.value) {
        return;
    }
    await generateContent(
        process.value.identity,
        EntityType.PROCESS,
        description.value?.updateContent.bind(description.value)
    );
};

const hasAnyContent = computed(() => {
    return Boolean(
        definition.value?.content ||
            description.value?.content ||
            artefacts.value?.length > 0
    );
});
</script>

<style scoped lang="scss">
.process-definition {
    height: 600px;
    max-height: 75vh;
    display: flex;
    padding: 0;
}

.process-definition-overlay {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    padding: 0.5rem 1rem;
    transform: translateY(-50%);
    z-index: 2;
    text-align: center;
    display: inline-block;
}

.process-elements-panel {
    --p-panel-toggleable-header-padding: 0;
    border: none;
}

.description {
    z-index: 3;
}

.archived-data-table {
    margin-top: 1rem;
}
</style>
